import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../utils/cn';
import { getAppearance } from '../utils/appearance';

export interface ILMSPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

// Rows per page to start with - chosen on Administration -> Appearance.
export const DEFAULT_PAGE_SIZE: number = getAppearance().page_size;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export function ILMSPagination({ page, pageSize, total, onPageChange, onPageSizeChange, pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS, className }: ILMSPaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className={cn('flex flex-col sm:flex-row items-center justify-between gap-3 pt-3', className)}>
      <div className="flex items-center gap-3">
        <p className="text-secondary text-ink-500">
          Showing <span className="font-medium text-ink-700">{from}–{to}</span> of{' '}
          <span className="font-medium text-ink-700">{total}</span>
        </p>
        {onPageSizeChange && (
          <label className="flex items-center gap-1.5 text-secondary text-ink-500">
            Rows
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-8 rounded-md border border-ink-300/40 bg-white px-1.5 text-secondary"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="h-9 w-9 flex items-center justify-center rounded-md border border-ink-300/40 disabled:opacity-40 hover:bg-surface-muted"
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-secondary px-2 tabular-nums">
          {page} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="h-9 w-9 flex items-center justify-center rounded-md border border-ink-300/40 disabled:opacity-40 hover:bg-surface-muted"
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
